import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    AppBar,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    InputAdornment,
    Pagination,
    Paper,
    Stack,
    TextField,
    Toolbar,
    Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import {
    PeopleAlt,
} from "@material-ui/icons";

import {
    PRIMARY_COLOR,
} from "./../util/constants";

import {
    POLLING_SPEED,
    usePolling,
} from "../util/polling";

import {
    validateNonDEMobileEmptyPhoneNumber,
    validateNonEmptyAlphanumericString,
    validatePassword,
} from "../api/validation_api";

import {
    createUserAPI,
    fetchUserAPI,
    fetchUsersAPI,
    isUsernameAvailableAPI,
} from "../api/user_api";

import UserEditor from
    "../fragments/UserEditor";


export const MSG_ERROR_OCCURED =
    "Es trat ein Fehler auf.";


const FORM_FIELD = Object.freeze({
    USERNAME:
        "username",

    PASSWORD:
        "password",

    REPEATED_PASSWORD:
        "repeatedPassword",

    MOBILE_PHONE_NUMBER:
        "mobilePhoneNumber",

    IS_ADMIN:
        "isAdmin",

    IS_SELLER:
        "isSeller",

    IS_BUYER:
        "isBuyer",
});


const VALIDATION_STATUS = Object.freeze({
    IDLE:
        "idle",

    CHECKING:
        "checking",

    VALID:
        "valid",

    INVALID:
        "invalid",

    ERROR:
        "error",
});


const CONFIG = Object.freeze({
    validationDelay:
        500,

    emptyHelperText:
        " ",

    topSpacing:
        "50px",

    headerPadding:
        "25px",

    pageSpacing:
        5,

    contentSpacing:
        7,

    formSpacing:
        2.5,

    dialogStackSpacing:
        3,

    fullWidthColumns:
        12,

    halfWidthColumns:
        6,

    validationProgressSize:
        18,
});


const BOOLEAN_RESPONSE_KEYS =
    Object.freeze([
        "valid",
        "result",
        "success",
        "value",
    ]);


const USERNAME_AVAILABILITY_KEYS =
    Object.freeze([
        "available",
        "isAvailable",
    ]);


const USER_TEXT = Object.freeze({
    page: Object.freeze({
        title:
            "Benutzer verwalten",

        addButton:
            "Hinzufügen",

        searchPlaceholder:
            "Benutzer durchsuchen",

        noUsers:
            "Keine passenden Benutzer gefunden.",

        loadError:
            "Benutzer konnten nicht geladen werden.",

        editButton:
            "Bearbeiten",
    }),

    table: Object.freeze({
        username:
            "Benutzername",

        name:
            "Name",

        businessName:
            "Firma",

        access:
            "Rollen / Status",

        action:
            "Aktion",
    }),

    editor: Object.freeze({
        title:
            "Benutzer bearbeiten",

        close:
            "Benutzereditor schließen",
    }),

    dialog: Object.freeze({
        title:
            "Benutzer hinzufügen",

        description:
            "Zugangsdaten und Berechtigungen für den neuen Benutzer festlegen.",

        userDataSection:
            "Benutzerdaten",

        rolesSection:
            "Rollen",

        rolesDescription:
            "Mindestens eine Rolle muss ausgewählt sein.",

        cancelButton:
            "Abbrechen",

        submittingButton:
            "Benutzer wird angelegt …",
    }),

    labels: Object.freeze({
        username:
            "Benutzername",

        mobilePhoneNumber:
            "Mobilfunknummer",

        password:
            "Passwort",

        repeatedPassword:
            "Passwort wiederholen",

        admin:
            "Administrator",

        buyer:
            "Käufer",

        seller:
            "Verkäufer",

        active:
            "Aktiv",

        disabled:
            "Deaktiviert",
    }),

    messages: Object.freeze({
        changesSaved:
            "Änderungen gespeichert.",

        correctHighlightedFields:
            "Bitte korrigieren Sie zunächst die markierten Eingaben.",

        selectAtLeastOneRole:
            "Bitte mindestens eine Rolle auswählen.",

        usernameChecking:
            "Benutzername wird geprüft …",

        usernameInvalidCharacters:
            "Der Benutzername enthält ungültige Zeichen.",

        usernameAvailabilityError:
            "Die Verfügbarkeit konnte nicht geprüft werden.",

        usernameAvailable:
            "Der Benutzername ist verfügbar.",

        usernameAlreadyExists:
            "Der Benutzername ist bereits vergeben.",

        usernameValidationError:
            "Der Benutzername konnte nicht geprüft werden.",

        mobilePhoneChecking:
            "Mobilfunknummer wird geprüft …",

        mobilePhoneInvalid:
            "Bitte eine gültige deutsche Mobilfunknummer eingeben.",

        mobilePhoneValidationError:
            "Die Mobilfunknummer konnte nicht geprüft werden.",

        passwordChecking:
            "Passwort wird geprüft …",

        passwordInvalid:
            "Das Passwort erfüllt die Anforderungen nicht.",

        passwordValidationError:
            "Das Passwort konnte nicht geprüft werden.",

        repeatedPasswordMismatch:
            "Die Passwörter stimmen nicht überein.",

        repeatedPasswordChecking:
            "Passwortwiederholung wird geprüft …",

        repeatedPasswordMatch:
            "Die Passwörter stimmen überein.",

        repeatedPasswordInvalid:
            "Das wiederholte Passwort ist nicht gültig.",

        repeatedPasswordValidationError:
            "Die Passwortwiederholung konnte nicht geprüft werden.",

        validationResponseInvalid:
            "Die Antwort der Validierungs-API konnte nicht verarbeitet werden.",

        validationHttpErrorPrefix:
            "Validierungsfehler: HTTP",
    }),
});


const INITIAL_FORM = Object.freeze({
    [FORM_FIELD.USERNAME]:
        "",

    [FORM_FIELD.PASSWORD]:
        "",

    [FORM_FIELD.REPEATED_PASSWORD]:
        "",

    [FORM_FIELD.MOBILE_PHONE_NUMBER]:
        "",

    [FORM_FIELD.IS_ADMIN]:
        false,

    [FORM_FIELD.IS_SELLER]:
        false,

    [FORM_FIELD.IS_BUYER]:
        true,
});


const STYLE = Object.freeze({
    topSpacer: {
        height:
            CONFIG.topSpacing,
    },

    header: {
        p:
            CONFIG.headerPadding,

        backgroundColor:
            PRIMARY_COLOR,

        color:
            "white",

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "space-between",

        gap:
            2,
    },

    headerTitleContainer: {
        display:
            "flex",

        alignItems:
            "center",

        gap:
            1.5,
    },

    headerIcon: {
        color:
            "white",

        fontSize:
            30,
    },

    addButton: {
        ml:
            "auto",

        backgroundColor:
            "white",

        color:
            PRIMARY_COLOR,

        whiteSpace:
            "nowrap",

        "&:hover": {
            backgroundColor:
                "grey.100",
        },
    },

    dialogTitle: {
        pb:
            1,
    },

    dialogDescription: {
        mt:
            0.5,
    },

    userDataSectionTitle: {
        mb:
            2,
    },

    rolesSectionTitle: {
        mb:
            1,
    },

    rolesDescription: {
        mb:
            1.5,
    },

    roleError: {
        mt:
            1.5,
    },

    dialogActions: {
        px:
            3,

        py:
            2,
    },
});


const COMPONENT_PROPS = Object.freeze({
    pageGrid: {
        container:
            true,

        spacing:
            CONFIG.pageSpacing,
    },

    contentGrid: {
        container:
            true,

        spacing:
            CONFIG.contentSpacing,
    },

    fullWidthGridItem: {
        item:
            true,

        xs:
            CONFIG.fullWidthColumns,
    },

    formGrid: {
        container:
            true,

        spacing:
            CONFIG.formSpacing,
    },

    formGridItem: {
        item:
            true,

        xs:
            CONFIG.fullWidthColumns,

        sm:
            CONFIG.halfWidthColumns,
    },

    pageTitle: {
        variant:
            "h6",

        component:
            "h1",
    },

    addButton: {
        variant:
            "contained",
    },

    dialog: {
        maxWidth:
            "sm",

        fullWidth:
            true,
    },

    dialogTitleText: {
        variant:
            "h5",

        component:
            "div",
    },

    dialogDescription: {
        variant:
            "body2",

        color:
            "text.secondary",
    },

    dialogContent: {
        dividers:
            true,
    },

    dialogStack: {
        spacing:
            CONFIG.dialogStackSpacing,
    },

    sectionTitle: {
        variant:
            "subtitle1",
    },

    sectionDescription: {
        variant:
            "body2",

        color:
            "text.secondary",
    },

    formGroup: {
        row:
            true,
    },

    roleError: {
        severity:
            "error",
    },

    submitButton: {
        variant:
            "contained",
    },

    validationProgress: {
        size:
            CONFIG.validationProgressSize,
    },

    submitProgress: {
        size:
            CONFIG.validationProgressSize,

        color:
            "inherit",
    },
});


const FIELD_CONFIG = Object.freeze({
    [FORM_FIELD.USERNAME]:
        Object.freeze({
            label:
                USER_TEXT.labels.username,

            autoComplete:
                "off",

            autoFocus:
                true,

            required:
                true,

            fullWidth:
                true,
        }),

    [FORM_FIELD.MOBILE_PHONE_NUMBER]:
        Object.freeze({
            label:
                USER_TEXT.labels.mobilePhoneNumber,

            autoComplete:
                "tel",

            required:
                true,

            fullWidth:
                true,
        }),

    [FORM_FIELD.PASSWORD]:
        Object.freeze({
            label:
                USER_TEXT.labels.password,

            type:
                "password",

            autoComplete:
                "new-password",

            required:
                true,

            fullWidth:
                true,
        }),

    [FORM_FIELD.REPEATED_PASSWORD]:
        Object.freeze({
            label:
                USER_TEXT.labels.repeatedPassword,

            type:
                "password",

            autoComplete:
                "new-password",

            required:
                true,

            fullWidth:
                true,
        }),
});


const ROLE_CONFIG =
    Object.freeze([
        Object.freeze({
            fieldName:
                FORM_FIELD.IS_ADMIN,

            label:
                USER_TEXT.labels.admin,
        }),

        Object.freeze({
            fieldName:
                FORM_FIELD.IS_BUYER,

            label:
                USER_TEXT.labels.buyer,
        }),

        Object.freeze({
            fieldName:
                FORM_FIELD.IS_SELLER,

            label:
                USER_TEXT.labels.seller,
        }),
    ]);


const ADD_ICON = (
    <AddIcon />
);


/*
 * =========================================================
 * USER TABLE
 * =========================================================
 */

const REGISTER_STYLE = Object.freeze({
    surface: {
        width:
            "100%",

        overflow:
            "hidden",

        borderRadius:
            3,

        borderColor:
            "rgba(12, 5, 109, 0.14)",

        boxShadow:
            "0 12px 30px rgba(12, 5, 109, 0.06)",
    },

    columnHeader: {
        display: {
            xs:
                "none",

            md:
                "grid",
        },

        alignItems:
            "center",

        gap:
            2,

        px:
            3,

        py:
            1.5,

        bgcolor:
            "rgba(12,5,109,.035)",

        borderBottom:
            "1px solid rgba(12,5,109,.09)",
    },

    row: {
        px: {
            xs:
                2,

            sm:
                3,
        },

        py: {
            xs:
                2,

            sm:
                2.5,
        },

        transition:
            "background-color .15s ease",

        "&:hover": {
            bgcolor:
                "rgba(12,5,109,.022)",
        },
    },

    label: {
        display: {
            xs:
                "block",

            md:
                "none",
        },

        mb:
            0.45,

        color:
            "text.secondary",
    },
});


const REGISTER_PAGE_SIZE =
    5;


const USER_TABLE_COLUMNS =
    "minmax(190px,1.05fr) minmax(210px,1.1fr) minmax(230px,1.2fr) minmax(260px,1.35fr) 145px";


function StatusPill({
    children,
    muted = false,
    error = false,
}) {
    return (
        <Box
            component="span"
            sx={{
                display:
                    "inline-flex",

                alignItems:
                    "center",

                width:
                    "fit-content",

                px:
                    1.15,

                py:
                    0.5,

                borderRadius:
                    "999px",

                border:
                    "1px solid",

                borderColor:
                    error
                        ? "rgba(211,47,47,.26)"
                        : muted
                            ? "rgba(0,0,0,.14)"
                            : "rgba(12,5,109,.22)",

                bgcolor:
                    error
                        ? "rgba(211,47,47,.055)"
                        : muted
                            ? "rgba(0,0,0,.035)"
                            : "rgba(12,5,109,.055)",

                color:
                    error
                        ? "error.main"
                        : muted
                            ? "text.secondary"
                            : "primary.main",

                fontSize:
                    "0.75rem",

                lineHeight:
                    1.35,

                whiteSpace:
                    "nowrap",
            }}
        >
            {children}
        </Box>
    );
}


function RegisterPagination({
    page,
    pageCount,
    total,
    pageSize = REGISTER_PAGE_SIZE,
    onChange,
}) {
    if (
        pageCount <= 1
    ) {
        return null;
    }

    const first =
        (page - 1)
        * pageSize
        + 1;

    const last =
        Math.min(
            page * pageSize,
            total,
        );

    return (
        <Box
            sx={{
                px: {
                    xs:
                        1.75,

                    sm:
                        3,
                },

                py: {
                    xs:
                        1,

                    sm:
                        1.5,
                },

                borderTop:
                    "1px solid rgba(12,5,109,.09)",

                display:
                    "flex",

                alignItems:
                    "center",

                justifyContent:
                    "space-between",

                gap:
                    2,

                flexWrap:
                    "wrap",

                bgcolor:
                    "rgba(12,5,109,.018)",
            }}
        >
            <Typography
                variant="caption"
                color="text.secondary"
            >
                {first}–{last} von {total}
            </Typography>

            <Box
                sx={{
                    display: {
                        xs:
                            "flex",

                        sm:
                            "none",
                    },

                    alignItems:
                        "center",

                    gap:
                        0.75,
                }}
            >
                <IconButton
                    size="small"
                    aria-label="Vorherige Seite"
                    disabled={
                        page <= 1
                    }
                    onClick={() => {
                        onChange(
                            Math.max(
                                1,
                                page - 1,
                            ),
                        );
                    }}
                    sx={{
                        width:
                            40,

                        height:
                            40,

                        color:
                            "primary.main",
                    }}
                >
                    <ChevronLeftIcon />
                </IconButton>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        minWidth:
                            48,

                        textAlign:
                            "center",

                        fontVariantNumeric:
                            "tabular-nums",
                    }}
                >
                    {page} / {pageCount}
                </Typography>

                <IconButton
                    size="small"
                    aria-label="Nächste Seite"
                    disabled={
                        page
                        >= pageCount
                    }
                    onClick={() => {
                        onChange(
                            Math.min(
                                pageCount,
                                page + 1,
                            ),
                        );
                    }}
                    sx={{
                        width:
                            40,

                        height:
                            40,

                        color:
                            "primary.main",
                    }}
                >
                    <ChevronRightIcon />
                </IconButton>
            </Box>

            <Box
                sx={{
                    display: {
                        xs:
                            "none",

                        sm:
                            "block",
                    },
                }}
            >
                <Pagination
                    page={page}
                    count={pageCount}
                    onChange={(
                        _,
                        nextPage,
                    ) => {
                        onChange(
                            nextPage,
                        );
                    }}
                    size="small"
                    color="primary"
                    showFirstButton
                    showLastButton
                />
            </Box>
        </Box>
    );
}


function RegisterSearchField({
    value,
    onChange,
    placeholder = "Suchen",
}) {
    return (
        <TextField
            value={value}
            onChange={(event) => {
                onChange(
                    event.target.value,
                );
            }}
            placeholder={placeholder}
            size="small"
            variant="outlined"
            inputProps={{
                "aria-label":
                    placeholder,
            }}
            InputProps={{
                startAdornment: (
                    <InputAdornment
                        position="start"
                    >
                        <SearchIcon
                            sx={{
                                fontSize:
                                    18,

                                color:
                                    "text.secondary",
                            }}
                        />
                    </InputAdornment>
                ),

                endAdornment:
                    value
                        ? (
                            <InputAdornment
                                position="end"
                            >
                                <IconButton
                                    size="small"
                                    aria-label="Suche leeren"
                                    onClick={() => {
                                        onChange(
                                            "",
                                        );
                                    }}
                                    sx={{
                                        width:
                                            26,

                                        height:
                                            26,

                                        color:
                                            "text.secondary",
                                    }}
                                >
                                    <CloseIcon
                                        sx={{
                                            fontSize:
                                                16,
                                        }}
                                    />
                                </IconButton>
                            </InputAdornment>
                        )
                        : null,
            }}
            sx={{
                width: {
                    xs:
                        "100%",

                    sm:
                        290,
                },

                "& .MuiOutlinedInput-root": {
                    bgcolor:
                        "rgba(255,255,255,.92)",

                    borderRadius:
                        2,
                },

                "& .MuiOutlinedInput-input": {
                    py:
                        1.05,
                },
            }}
        />
    );
}


function RegisterHeaderSearch({
    value,
    onChange,
    placeholder,
}) {
    return (
        <Box
            sx={{
                px: {
                    xs:
                        1.75,

                    sm:
                        3,
                },

                pt:
                    1.35,

                pb:
                    0.5,

                display:
                    "flex",

                justifyContent:
                    "flex-end",

                bgcolor:
                    "rgba(12,5,109,.035)",
            }}
        >
            <RegisterSearchField
                value={value}
                onChange={onChange}
                placeholder={placeholder}
            />
        </Box>
    );
}


function UserTableSkeleton() {
    return (
        <Paper
            variant="outlined"
            sx={
                REGISTER_STYLE.surface
            }
        >
            <Box
                sx={{
                    px:
                        3,

                    py:
                        5,

                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "center",

                    gap:
                        1.5,
                }}
            >
                <CircularProgress
                    size={22}
                />

                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    Benutzer werden geladen …
                </Typography>
            </Box>
        </Paper>
    );
}


const normalizeUserIds = (
    response,
) => {
    if (
        !Array.isArray(
            response,
        )
    ) {
        return [];
    }

    const knownIds =
        new Set();

    return response.filter(
        (userId) => {
            if (
                userId === null
                || userId
                === undefined
            ) {
                return false;
            }

            const key =
                String(userId);

            if (
                knownIds.has(
                    key,
                )
            ) {
                return false;
            }

            knownIds.add(
                key,
            );

            return true;
        },
    );
};


const loadUser =
    async (
        userId,
        accessToken,
    ) => {
        const response =
            await fetchUserAPI(
                userId,
                accessToken,
            );

        if (
            !response.ok
        ) {
            throw new Error(
                `${MSG_ERROR_OCCURED} ${response.status}`,
            );
        }

        const user =
            await response.json();

        return {
            ...user,

            id:
                user?.id
                ?? user?.userId
                ?? userId,
        };
    };


const useUsersPolling = ({
    accessToken,
    showMessage,
    username,
}) => {
    const [
        users,
        setUsers,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        reloadVersion,
        setReloadVersion,
    ] = useState(0);


    const pollingRestartKey =
        useMemo(
            () => ({
                accessToken,
                reloadVersion,
                username,
            }),
            [
                accessToken,
                reloadVersion,
                username,
            ],
        );


    const pollUsers =
        useCallback(
            async ({
                isPollingActive,
            }) => {
                if (
                    !accessToken
                ) {
                    if (
                        isPollingActive()
                    ) {
                        setUsers(
                            [],
                        );

                        setLoading(
                            false,
                        );
                    }

                    return;
                }

                try {
                    const response =
                        await fetchUsersAPI(
                            accessToken,
                        );

                    if (
                        !isPollingActive()
                    ) {
                        return;
                    }

                    if (
                        !response.ok
                    ) {
                        throw new Error(
                            `${MSG_ERROR_OCCURED} ${response.status}`,
                        );
                    }

                    const userIds =
                        normalizeUserIds(
                            await response.json(),
                        );

                    const userResults =
                        await Promise.allSettled(
                            userIds.map(
                                (userId) => (
                                    loadUser(
                                        userId,
                                        accessToken,
                                    )
                                ),
                            ),
                        );

                    if (
                        !isPollingActive()
                    ) {
                        return;
                    }

                    const loadedUsers =
                        userResults
                            .filter(
                                (result) => (
                                    result.status
                                    === "fulfilled"
                                ),
                            )
                            .map(
                                (result) => (
                                    result.value
                                ),
                            );

                    setUsers(
                        loadedUsers,
                    );
                } catch (error) {
                    if (
                        isPollingActive()
                    ) {
                        showMessage(
                            error?.message
                            || USER_TEXT
                                .page
                                .loadError,
                        );
                    }
                } finally {
                    if (
                        isPollingActive()
                    ) {
                        setLoading(
                            false,
                        );
                    }
                }
            },
            [
                accessToken,
                showMessage,
            ],
        );


    usePolling({
        poll:
            pollUsers,

        enabled:
            Boolean(
                accessToken,
            ),

        immediate:
            true,

        speed:
            POLLING_SPEED.medium,

        restartKey:
            pollingRestartKey,
    });


    const reloadUsers =
        useCallback(
            () => {
                setLoading(
                    true,
                );

                setReloadVersion(
                    (
                        currentVersion,
                    ) => (
                        currentVersion
                        + 1
                    ),
                );
            },
            [],
        );


    return {
        loading,
        reloadUsers,
        users,
    };
};


function UserRegister({
    users,
    loading,
    onEdit,
}) {
    const [
        page,
        setPage,
    ] = useState(1);

    const [
        searchQuery,
        setSearchQuery,
    ] = useState("");


    const filteredUsers =
        useMemo(
            () => {
                const query =
                    searchQuery
                        .trim()
                        .toLocaleLowerCase(
                            "de-DE",
                        );

                if (
                    !query
                ) {
                    return users;
                }

                return users.filter(
                    (user) => {
                        const values = [
                            user?.username,
                            user?.firstName,
                            user?.lastName,
                            user?.businessName,
                        ];

                        return values.some(
                            (value) => (
                                String(
                                    value
                                    ?? "",
                                )
                                    .toLocaleLowerCase(
                                        "de-DE",
                                    )
                                    .includes(
                                        query,
                                    )
                            ),
                        );
                    },
                );
            },
            [
                searchQuery,
                users,
            ],
        );


    const pageCount =
        Math.max(
            1,
            Math.ceil(
                filteredUsers.length
                / REGISTER_PAGE_SIZE,
            ),
        );


    const visibleUsers =
        filteredUsers.slice(
            (
                page - 1
            )
            * REGISTER_PAGE_SIZE,

            page
            * REGISTER_PAGE_SIZE,
        );


    useEffect(
        () => {
            if (
                page > pageCount
            ) {
                setPage(
                    pageCount,
                );
            }
        },
        [
            page,
            pageCount,
        ],
    );


    useEffect(
        () => {
            setPage(
                1,
            );
        },
        [
            searchQuery,
            users.length,
        ],
    );


    if (
        loading
        && users.length === 0
    ) {
        return (
            <UserTableSkeleton />
        );
    }


    return (
        <Paper
            variant="outlined"
            sx={
                REGISTER_STYLE.surface
            }
        >
            <RegisterHeaderSearch
                value={searchQuery}
                onChange={
                    setSearchQuery
                }
                placeholder={
                    USER_TEXT
                        .page
                        .searchPlaceholder
                }
            />

            <Box
                sx={{
                    ...REGISTER_STYLE
                        .columnHeader,

                    gridTemplateColumns:
                        USER_TABLE_COLUMNS,

                    pt:
                        1.1,
                }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                >
                    {
                        USER_TEXT
                            .table
                            .username
                    }
                </Typography>

                <Typography
                    variant="caption"
                    color="text.secondary"
                >
                    {
                        USER_TEXT
                            .table
                            .name
                    }
                </Typography>

                <Typography
                    variant="caption"
                    color="text.secondary"
                >
                    {
                        USER_TEXT
                            .table
                            .businessName
                    }
                </Typography>

                <Typography
                    variant="caption"
                    color="text.secondary"
                >
                    {
                        USER_TEXT
                            .table
                            .access
                    }
                </Typography>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        textAlign:
                            "right",
                    }}
                >
                    {
                        USER_TEXT
                            .table
                            .action
                    }
                </Typography>
            </Box>


            {
                visibleUsers.length
                    === 0
                    ? (
                        <Box
                            sx={{
                                px:
                                    3,

                                py:
                                    5,

                                textAlign:
                                    "center",
                            }}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {
                                    USER_TEXT
                                        .page
                                        .noUsers
                                }
                            </Typography>
                        </Box>
                    )
                    : visibleUsers.map(
                        (
                            user,
                            index,
                        ) => {
                            const fullName =
                                [
                                    user
                                        ?.firstName,

                                    user
                                        ?.lastName,
                                ]
                                    .filter(
                                        Boolean,
                                    )
                                    .join(
                                        " ",
                                    )
                                || "–";


                            const roles = [
                                user?.admin
                                    ? USER_TEXT
                                        .labels
                                        .admin
                                    : null,

                                user?.buyer
                                    ? USER_TEXT
                                        .labels
                                        .buyer
                                    : null,

                                user?.seller
                                    ? USER_TEXT
                                        .labels
                                        .seller
                                    : null,
                            ].filter(
                                Boolean,
                            );


                            return (
                                <Fragment
                                    key={
                                        user.id
                                    }
                                >
                                    {
                                        index > 0
                                            ? (
                                                <Divider />
                                            )
                                            : null
                                    }

                                    <Box
                                        sx={{
                                            ...REGISTER_STYLE
                                                .row,

                                            display:
                                                "grid",

                                            gridTemplateColumns: {
                                                xs:
                                                    "1fr",

                                                md:
                                                    USER_TABLE_COLUMNS,
                                            },

                                            gap: {
                                                xs:
                                                    2,

                                                md:
                                                    2,
                                            },

                                            alignItems: {
                                                xs:
                                                    "start",

                                                md:
                                                    "center",
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minWidth:
                                                    0,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={
                                                    REGISTER_STYLE
                                                        .label
                                                }
                                            >
                                                {
                                                    USER_TEXT
                                                        .table
                                                        .username
                                                }
                                            </Typography>

                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    overflowWrap:
                                                        "anywhere",
                                                }}
                                            >
                                                {
                                                    user
                                                        ?.username
                                                    || "–"
                                                }
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display:
                                                        "block",

                                                    mt:
                                                        0.35,
                                                }}
                                            >
                                                ID {user.id}
                                            </Typography>
                                        </Box>


                                        <Box
                                            sx={{
                                                minWidth:
                                                    0,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={
                                                    REGISTER_STYLE
                                                        .label
                                                }
                                            >
                                                {
                                                    USER_TEXT
                                                        .table
                                                        .name
                                                }
                                            </Typography>

                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    overflowWrap:
                                                        "anywhere",
                                                }}
                                            >
                                                {fullName}
                                            </Typography>
                                        </Box>


                                        <Box
                                            sx={{
                                                minWidth:
                                                    0,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={
                                                    REGISTER_STYLE
                                                        .label
                                                }
                                            >
                                                {
                                                    USER_TEXT
                                                        .table
                                                        .businessName
                                                }
                                            </Typography>

                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    overflowWrap:
                                                        "anywhere",
                                                }}
                                            >
                                                {
                                                    user
                                                        ?.businessName
                                                    || "–"
                                                }
                                            </Typography>
                                        </Box>


                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={
                                                    REGISTER_STYLE
                                                        .label
                                                }
                                            >
                                                {
                                                    USER_TEXT
                                                        .table
                                                        .access
                                                }
                                            </Typography>

                                            <Box
                                                sx={{
                                                    display:
                                                        "flex",

                                                    gap:
                                                        0.75,

                                                    flexWrap:
                                                        "wrap",
                                                }}
                                            >
                                                {
                                                    roles.length
                                                        > 0
                                                        ? roles.map(
                                                            (
                                                                role,
                                                            ) => (
                                                                <StatusPill
                                                                    key={
                                                                        role
                                                                    }
                                                                >
                                                                    {
                                                                        role
                                                                    }
                                                                </StatusPill>
                                                            ),
                                                        )
                                                        : (
                                                            <StatusPill
                                                                muted
                                                            >
                                                                Keine Rolle
                                                            </StatusPill>
                                                        )
                                                }

                                                <StatusPill
                                                    muted={
                                                        !user
                                                            ?.disabled
                                                    }
                                                    error={
                                                        Boolean(
                                                            user
                                                                ?.disabled,
                                                        )
                                                    }
                                                >
                                                    {
                                                        user
                                                            ?.disabled
                                                            ? USER_TEXT
                                                                .labels
                                                                .disabled
                                                            : USER_TEXT
                                                                .labels
                                                                .active
                                                    }
                                                </StatusPill>
                                            </Box>
                                        </Box>


                                        <Box
                                            sx={{
                                                display:
                                                    "flex",

                                                justifyContent: {
                                                    xs:
                                                        "flex-start",

                                                    md:
                                                        "flex-end",
                                                },
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                startIcon={
                                                    <AccountCircleIcon />
                                                }
                                                onClick={() => {
                                                    onEdit(
                                                        user,
                                                    );
                                                }}
                                            >
                                                {
                                                    USER_TEXT
                                                        .page
                                                        .editButton
                                                }
                                            </Button>
                                        </Box>
                                    </Box>
                                </Fragment>
                            );
                        },
                    )
            }


            <RegisterPagination
                page={page}
                pageCount={
                    pageCount
                }
                total={
                    filteredUsers
                        .length
                }
                onChange={
                    setPage
                }
            />
        </Paper>
    );
}


/*
 * =========================================================
 * FULLSCREEN USER EDITOR
 * =========================================================
 */

function UserEditorDialog({
    appAPI,
    contextAPI,
    user,
    open,
    onClose,
}) {
    if (
        !user
    ) {
        return null;
    }


    const fullName =
        [
            user?.firstName,
            user?.lastName,
        ]
            .filter(
                Boolean,
            )
            .join(
                " ",
            );


    const subtitle =
        fullName
        || user?.businessName
        || user?.username
        || `ID ${user.id}`;


    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            sx={{
                zIndex: 100000,
                "& .MuiDialog-paper": {
                    fontFamily:
                        "Arial, Helvetica, sans-serif",
                },

                "& .MuiTypography-root": {
                    fontFamily:
                        "Arial, Helvetica, sans-serif",
                },

                "& .MuiButton-root": {
                    fontFamily:
                        "Arial, Helvetica, sans-serif",
                },

                "& .MuiInputBase-root": {
                    fontFamily:
                        "Arial, Helvetica, sans-serif",
                },
            }}
        >
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor:
                        PRIMARY_COLOR,
                }}
            >
                <Toolbar
                    sx={{
                        gap:
                            1.5,
                    }}
                >
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        aria-label={
                            USER_TEXT
                                .editor
                                .close
                        }
                    >
                        <CloseIcon />
                    </IconButton>

                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            variant="h6"
                            component="div"
                        >
                            {
                                USER_TEXT
                                    .editor
                                    .title
                            }
                        </Typography>


                    </Box>
                </Toolbar>
            </AppBar>


            <DialogContent
                sx={{
                    p: 0,
                    mt: 10,
                    mb: 10,
                    bgcolor:
                        "background.default",
                }}
            >
                <Box
                    sx={{
                        width:
                            "90%",

                        maxWidth:
                            1600,

                        mx:
                            "auto",

                    }}  
                >
                    <UserEditor
                        key={
                            user.id
                        }
                        appAPI={
                            appAPI
                        }
                        contextAPI={
                            contextAPI
                        }
                        userId={
                            user.id
                        }
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
}


/*
 * =========================================================
 * ADD USER
 * =========================================================
 */

const createFieldValidation = (
    status =
        VALIDATION_STATUS.IDLE,
    message =
        "",
) => ({
    status,
    message,
});


const createInitialValidation =
    () => ({
        [FORM_FIELD.USERNAME]:
            createFieldValidation(),

        [FORM_FIELD.PASSWORD]:
            createFieldValidation(),

        [FORM_FIELD.REPEATED_PASSWORD]:
            createFieldValidation(),

        [FORM_FIELD.MOBILE_PHONE_NUMBER]:
            createFieldValidation(),
    });


const createRoleContainerStyle = (
    showRoleError,
) => ({
    border:
        1,

    borderColor:
        showRoleError
            ? "error.main"
            : "divider",

    borderRadius:
        2,

    px:
        2,

    py:
        1,

    bgcolor:
        "background.default",
});


const extractBoolean = (
    value,
    additionalKeys = [],
) => {
    if (
        typeof value
        === "boolean"
    ) {
        return value;
    }

    if (
        typeof value
        === "string"
    ) {
        const normalizedValue =
            value
                .trim()
                .toLowerCase();

        if (
            normalizedValue
            === "true"
        ) {
            return true;
        }

        if (
            normalizedValue
            === "false"
        ) {
            return false;
        }
    }

    if (
        value
        && typeof value
        === "object"
    ) {
        const possibleKeys = [
            ...additionalKeys,
            ...BOOLEAN_RESPONSE_KEYS,
        ];

        for (
            const key
            of possibleKeys
        ) {
            if (
                typeof value[key]
                === "boolean"
            ) {
                return value[key];
            }
        }
    }

    return null;
};


const readValidationResponse =
    async (
        response,
    ) => {
        if (
            !response.ok
        ) {
            if (
                response.status
                >= 400
                && response.status
                < 500
            ) {
                return false;
            }

            throw new Error(
                `${USER_TEXT
                    .messages
                    .validationHttpErrorPrefix
                } ${response.status}`,
            );
        }

        const responseText =
            await response.text();

        if (
            !responseText.trim()
        ) {
            return true;
        }

        let parsedValue =
            responseText;

        try {
            parsedValue =
                JSON.parse(
                    responseText,
                );
        } catch {
            // Plain-Text-Boolean wird unterstützt.
        }

        const result =
            extractBoolean(
                parsedValue,
            );

        if (
            result === null
        ) {
            throw new Error(
                USER_TEXT
                    .messages
                    .validationResponseInvalid,
            );
        }

        return result;
    };


const useAddUserDialog =
    () => {
        const [
            isOpen,
            setIsOpen,
        ] = useState(false);

        const openDialog =
            useCallback(
                () => {
                    setIsOpen(
                        true,
                    );
                },
                [],
            );

        const closeDialog =
            useCallback(
                () => {
                    setIsOpen(
                        false,
                    );
                },
                [],
            );

        return {
            closeDialog,
            isOpen,
            openDialog,
        };
    };


const useUsernameValidation = ({
    accessToken,
    open,
    showMessage,
    updateValidation,
    username,
}) => {
    useEffect(
        () => {
            const usernameValue =
                username.trim();

            if (
                !open
                || !usernameValue
            ) {
                updateValidation(
                    FORM_FIELD.USERNAME,
                    createFieldValidation(),
                );

                return undefined;
            }

            let isCancelled =
                false;

            updateValidation(
                FORM_FIELD.USERNAME,
                createFieldValidation(
                    VALIDATION_STATUS.CHECKING,
                    USER_TEXT
                        .messages
                        .usernameChecking,
                ),
            );

            const timeoutId =
                window.setTimeout(
                    async () => {
                        try {
                            const validationResponse =
                                await validateNonEmptyAlphanumericString(
                                    usernameValue,
                                );

                            const isValid =
                                await readValidationResponse(
                                    validationResponse,
                                );

                            if (
                                isCancelled
                            ) {
                                return;
                            }

                            if (
                                !isValid
                            ) {
                                updateValidation(
                                    FORM_FIELD.USERNAME,
                                    createFieldValidation(
                                        VALIDATION_STATUS.INVALID,
                                        USER_TEXT
                                            .messages
                                            .usernameInvalidCharacters,
                                    ),
                                );

                                return;
                            }

                            const availabilityResponse =
                                await isUsernameAvailableAPI(
                                    usernameValue,
                                    accessToken,
                                    showMessage,
                                );

                            if (
                                isCancelled
                            ) {
                                return;
                            }

                            const isAvailable =
                                extractBoolean(
                                    availabilityResponse,
                                    USERNAME_AVAILABILITY_KEYS,
                                );

                            if (
                                isAvailable
                                === null
                            ) {
                                updateValidation(
                                    FORM_FIELD.USERNAME,
                                    createFieldValidation(
                                        VALIDATION_STATUS.ERROR,
                                        USER_TEXT
                                            .messages
                                            .usernameAvailabilityError,
                                    ),
                                );

                                return;
                            }

                            updateValidation(
                                FORM_FIELD.USERNAME,
                                createFieldValidation(
                                    isAvailable
                                        ? VALIDATION_STATUS.VALID
                                        : VALIDATION_STATUS.INVALID,

                                    isAvailable
                                        ? USER_TEXT
                                            .messages
                                            .usernameAvailable
                                        : USER_TEXT
                                            .messages
                                            .usernameAlreadyExists,
                                ),
                            );
                        } catch (
                        error
                        ) {
                            if (
                                isCancelled
                            ) {
                                return;
                            }

                            updateValidation(
                                FORM_FIELD.USERNAME,
                                createFieldValidation(
                                    VALIDATION_STATUS.ERROR,
                                    error.message
                                    || USER_TEXT
                                        .messages
                                        .usernameValidationError,
                                ),
                            );
                        }
                    },
                    CONFIG
                        .validationDelay,
                );

            return () => {
                isCancelled =
                    true;

                window.clearTimeout(
                    timeoutId,
                );
            };
        },
        [
            accessToken,
            open,
            showMessage,
            updateValidation,
            username,
        ],
    );
};


const useMobilePhoneValidation = ({
    mobilePhoneNumber,
    open,
    updateValidation,
}) => {
    useEffect(
        () => {
            const normalizedPhoneNumber =
                mobilePhoneNumber.trim();

            if (
                !open
                || !normalizedPhoneNumber
            ) {
                updateValidation(
                    FORM_FIELD
                        .MOBILE_PHONE_NUMBER,

                    createFieldValidation(),
                );

                return undefined;
            }

            let isCancelled =
                false;

            updateValidation(
                FORM_FIELD
                    .MOBILE_PHONE_NUMBER,

                createFieldValidation(
                    VALIDATION_STATUS.CHECKING,
                    USER_TEXT
                        .messages
                        .mobilePhoneChecking,
                ),
            );

            const timeoutId =
                window.setTimeout(
                    async () => {
                        try {
                            const response =
                                await validateNonDEMobileEmptyPhoneNumber(
                                    normalizedPhoneNumber,
                                );

                            const isValid =
                                await readValidationResponse(
                                    response,
                                );

                            if (
                                isCancelled
                            ) {
                                return;
                            }

                            updateValidation(
                                FORM_FIELD
                                    .MOBILE_PHONE_NUMBER,

                                createFieldValidation(
                                    isValid
                                        ? VALIDATION_STATUS.VALID
                                        : VALIDATION_STATUS.INVALID,

                                    isValid
                                        ? ""
                                        : USER_TEXT
                                            .messages
                                            .mobilePhoneInvalid,
                                ),
                            );
                        } catch (
                        error
                        ) {
                            if (
                                isCancelled
                            ) {
                                return;
                            }

                            updateValidation(
                                FORM_FIELD
                                    .MOBILE_PHONE_NUMBER,

                                createFieldValidation(
                                    VALIDATION_STATUS.ERROR,

                                    error.message
                                    || USER_TEXT
                                        .messages
                                        .mobilePhoneValidationError,
                                ),
                            );
                        }
                    },
                    CONFIG
                        .validationDelay,
                );

            return () => {
                isCancelled =
                    true;

                window.clearTimeout(
                    timeoutId,
                );
            };
        },
        [
            mobilePhoneNumber,
            open,
            updateValidation,
        ],
    );
};


const usePasswordValidation = ({
    open,
    password,
    updateValidation,
}) => {
    useEffect(
        () => {
            if (
                !open
                || !password
            ) {
                updateValidation(
                    FORM_FIELD.PASSWORD,
                    createFieldValidation(),
                );

                return undefined;
            }

            let isCancelled =
                false;

            updateValidation(
                FORM_FIELD.PASSWORD,

                createFieldValidation(
                    VALIDATION_STATUS.CHECKING,
                    USER_TEXT
                        .messages
                        .passwordChecking,
                ),
            );

            const timeoutId =
                window.setTimeout(
                    async () => {
                        try {
                            const response =
                                await validatePassword(
                                    password,
                                );

                            const isValid =
                                await readValidationResponse(
                                    response,
                                );

                            if (
                                isCancelled
                            ) {
                                return;
                            }

                            updateValidation(
                                FORM_FIELD.PASSWORD,

                                createFieldValidation(
                                    isValid
                                        ? VALIDATION_STATUS.VALID
                                        : VALIDATION_STATUS.INVALID,

                                    isValid
                                        ? ""
                                        : USER_TEXT
                                            .messages
                                            .passwordInvalid,
                                ),
                            );
                        } catch (
                        error
                        ) {
                            if (
                                isCancelled
                            ) {
                                return;
                            }

                            updateValidation(
                                FORM_FIELD.PASSWORD,

                                createFieldValidation(
                                    VALIDATION_STATUS.ERROR,

                                    error.message
                                    || USER_TEXT
                                        .messages
                                        .passwordValidationError,
                                ),
                            );
                        }
                    },
                    CONFIG
                        .validationDelay,
                );

            return () => {
                isCancelled =
                    true;

                window.clearTimeout(
                    timeoutId,
                );
            };
        },
        [
            open,
            password,
            updateValidation,
        ],
    );
};


const useRepeatedPasswordValidation = ({
    open,
    password,
    repeatedPassword,
    updateValidation,
}) => {
    useEffect(
        () => {
            if (
                !open
                || !repeatedPassword
            ) {
                updateValidation(
                    FORM_FIELD
                        .REPEATED_PASSWORD,

                    createFieldValidation(),
                );

                return undefined;
            }

            if (
                repeatedPassword
                !== password
            ) {
                updateValidation(
                    FORM_FIELD
                        .REPEATED_PASSWORD,

                    createFieldValidation(
                        VALIDATION_STATUS.INVALID,
                        USER_TEXT
                            .messages
                            .repeatedPasswordMismatch,
                    ),
                );

                return undefined;
            }

            let isCancelled =
                false;

            updateValidation(
                FORM_FIELD
                    .REPEATED_PASSWORD,

                createFieldValidation(
                    VALIDATION_STATUS.CHECKING,
                    USER_TEXT
                        .messages
                        .repeatedPasswordChecking,
                ),
            );

            const timeoutId =
                window.setTimeout(
                    async () => {
                        try {
                            const response =
                                await validatePassword(
                                    repeatedPassword,
                                );

                            const isValid =
                                await readValidationResponse(
                                    response,
                                );

                            if (
                                isCancelled
                            ) {
                                return;
                            }

                            updateValidation(
                                FORM_FIELD
                                    .REPEATED_PASSWORD,

                                createFieldValidation(
                                    isValid
                                        ? VALIDATION_STATUS.VALID
                                        : VALIDATION_STATUS.INVALID,

                                    isValid
                                        ? USER_TEXT
                                            .messages
                                            .repeatedPasswordMatch
                                        : USER_TEXT
                                            .messages
                                            .repeatedPasswordInvalid,
                                ),
                            );
                        } catch (
                        error
                        ) {
                            if (
                                isCancelled
                            ) {
                                return;
                            }

                            updateValidation(
                                FORM_FIELD
                                    .REPEATED_PASSWORD,

                                createFieldValidation(
                                    VALIDATION_STATUS.ERROR,

                                    error.message
                                    || USER_TEXT
                                        .messages
                                        .repeatedPasswordValidationError,
                                ),
                            );
                        }
                    },
                    CONFIG
                        .validationDelay,
                );

            return () => {
                isCancelled =
                    true;

                window.clearTimeout(
                    timeoutId,
                );
            };
        },
        [
            open,
            password,
            repeatedPassword,
            updateValidation,
        ],
    );
};


const useAddUserForm = ({
    accessToken,
    onClose,
    open,
    reload,
    showMessage,
}) => {
    const [
        form,
        setForm,
    ] = useState(
        INITIAL_FORM,
    );

    const [
        validation,
        setValidation,
    ] = useState(
        createInitialValidation,
    );

    const [
        isSubmitting,
        setIsSubmitting,
    ] = useState(false);

    const [
        showRoleError,
        setShowRoleError,
    ] = useState(false);


    const updateValidation =
        useCallback(
            (
                fieldName,
                nextValidation,
            ) => {
                setValidation(
                    (
                        currentValidation,
                    ) => ({
                        ...currentValidation,

                        [fieldName]:
                            nextValidation,
                    }),
                );
            },
            [],
        );


    const handleFieldChange =
        useCallback(
            (
                fieldName,
            ) => (
                event,
            ) => {
                    const {
                        value,
                    } = event.target;

                    setForm(
                        (
                            currentForm,
                        ) => ({
                            ...currentForm,

                            [fieldName]:
                                value,
                        }),
                    );
                },
            [],
        );


    const handleRoleChange =
        useCallback(
            (
                fieldName,
            ) => (
                event,
            ) => {
                    const {
                        checked,
                    } = event.target;

                    setForm(
                        (
                            currentForm,
                        ) => ({
                            ...currentForm,

                            [fieldName]:
                                checked,
                        }),
                    );

                    if (
                        checked
                    ) {
                        setShowRoleError(
                            false,
                        );
                    }
                },
            [],
        );


    const hasSelectedRole = (
        form[
        FORM_FIELD.IS_ADMIN
        ]
        || form[
        FORM_FIELD.IS_SELLER
        ]
        || form[
        FORM_FIELD.IS_BUYER
        ]
    );


    useUsernameValidation({
        accessToken,
        open,
        showMessage,
        updateValidation,

        username:
            form[
            FORM_FIELD.USERNAME
            ],
    });


    useMobilePhoneValidation({
        mobilePhoneNumber:
            form[
            FORM_FIELD
                .MOBILE_PHONE_NUMBER
            ],

        open,
        updateValidation,
    });


    usePasswordValidation({
        open,

        password:
            form[
            FORM_FIELD.PASSWORD
            ],

        updateValidation,
    });


    useRepeatedPasswordValidation({
        open,

        password:
            form[
            FORM_FIELD.PASSWORD
            ],

        repeatedPassword:
            form[
            FORM_FIELD
                .REPEATED_PASSWORD
            ],

        updateValidation,
    });


    const canSubmit = (
        validation[
            FORM_FIELD.USERNAME
        ].status
        === VALIDATION_STATUS.VALID

        && validation[
            FORM_FIELD.PASSWORD
        ].status
        === VALIDATION_STATUS.VALID

        && validation[
            FORM_FIELD
                .REPEATED_PASSWORD
        ].status
        === VALIDATION_STATUS.VALID

        && validation[
            FORM_FIELD
                .MOBILE_PHONE_NUMBER
        ].status
        === VALIDATION_STATUS.VALID

        && hasSelectedRole

        && !isSubmitting
    );


    const resetDialog =
        useCallback(
            () => {
                setForm(
                    INITIAL_FORM,
                );

                setValidation(
                    createInitialValidation(),
                );

                setShowRoleError(
                    false,
                );
            },
            [],
        );


    const handleClose =
        useCallback(
            () => {
                if (
                    isSubmitting
                ) {
                    return;
                }

                resetDialog();
                onClose();
            },
            [
                isSubmitting,
                onClose,
                resetDialog,
            ],
        );


    const createUser =
        useCallback(
            async () => {
                if (
                    !hasSelectedRole
                ) {
                    setShowRoleError(
                        true,
                    );

                    return;
                }

                if (
                    !canSubmit
                ) {
                    showMessage(
                        USER_TEXT
                            .messages
                            .correctHighlightedFields,
                    );

                    return;
                }

                setIsSubmitting(
                    true,
                );

                try {
                    const response =
                        await createUserAPI(
                            accessToken,

                            form[
                                FORM_FIELD.USERNAME
                            ].trim(),

                            form[
                            FORM_FIELD.PASSWORD
                            ],

                            form[
                                FORM_FIELD
                                    .MOBILE_PHONE_NUMBER
                            ].trim(),

                            form[
                            FORM_FIELD.IS_ADMIN
                            ],

                            form[
                            FORM_FIELD.IS_SELLER
                            ],

                            form[
                            FORM_FIELD.IS_BUYER
                            ],
                        );

                    if (
                        !response.ok
                    ) {
                        showMessage(
                            `${MSG_ERROR_OCCURED} ${response.status}`,
                        );

                        return;
                    }

                    showMessage(
                        USER_TEXT
                            .messages
                            .changesSaved,
                    );

                    reload?.();

                    resetDialog();

                    onClose();
                } catch (
                error
                ) {
                    showMessage(
                        error.toString(),
                    );
                } finally {
                    setIsSubmitting(
                        false,
                    );
                }
            },
            [
                accessToken,
                canSubmit,
                form,
                hasSelectedRole,
                onClose,
                reload,
                resetDialog,
                showMessage,
            ],
        );


    return {
        canSubmit,
        createUser,
        form,
        handleClose,
        handleFieldChange,
        handleRoleChange,
        isSubmitting,
        showRoleError,
        validation,
    };
};


const createTextFieldValidationProps = (
    fieldValidation,
) => {
    const hasError = [
        VALIDATION_STATUS.INVALID,
        VALIDATION_STATUS.ERROR,
    ].includes(
        fieldValidation.status,
    );

    const isChecking = (
        fieldValidation.status
        === VALIDATION_STATUS.CHECKING
    );

    const isValid = (
        fieldValidation.status
        === VALIDATION_STATUS.VALID
    );

    return {
        error:
            hasError,

        helperText:
            fieldValidation.message
            || CONFIG.emptyHelperText,

        FormHelperTextProps: {
            sx: {
                color:
                    isValid
                        ? "success.main"
                        : undefined,
            },
        },

        InputProps: {
            endAdornment:
                isChecking
                    ? (
                        <CircularProgress
                            {
                            ...COMPONENT_PROPS
                                .validationProgress
                            }
                        />
                    )
                    : undefined,
        },
    };
};


function UserFormField({
    fieldName,
    form,
    handleFieldChange,
    validation,
}) {
    const fieldConfig =
        FIELD_CONFIG[
        fieldName
        ];

    const validationProps =
        useMemo(
            () => (
                createTextFieldValidationProps(
                    validation[
                    fieldName
                    ],
                )
            ),
            [
                fieldName,
                validation,
            ],
        );

    return (
        <Grid
            {
            ...COMPONENT_PROPS
                .formGridItem
            }
        >
            <TextField
                {...fieldConfig}
                {...validationProps}
                value={
                    form[
                    fieldName
                    ]
                }
                onChange={
                    handleFieldChange(
                        fieldName,
                    )
                }
            />
        </Grid>
    );
}


function RoleCheckbox({
    fieldName,
    form,
    handleRoleChange,
    label,
}) {
    const checkbox = (
        <Checkbox
            checked={
                form[
                fieldName
                ]
            }
            onChange={
                handleRoleChange(
                    fieldName,
                )
            }
        />
    );

    return (
        <FormControlLabel
            control={
                checkbox
            }
            label={
                label
            }
        />
    );
}


function AddUser({
    accessToken,
    onClose,
    open,
    reload,
    showMessage,
}) {
    const {
        canSubmit,
        createUser,
        form,
        handleClose,
        handleFieldChange,
        handleRoleChange,
        isSubmitting,
        showRoleError,
        validation,
    } = useAddUserForm({
        accessToken,
        onClose,
        open,
        reload,
        showMessage,
    });


    const roleContainerStyle =
        useMemo(
            () => (
                createRoleContainerStyle(
                    showRoleError,
                )
            ),
            [
                showRoleError,
            ],
        );


    const submitIcon =
        isSubmitting
            ? (
                <CircularProgress
                    {
                    ...COMPONENT_PROPS
                        .submitProgress
                    }
                />
            )
            : undefined;


    const submitLabel =
        isSubmitting
            ? USER_TEXT
                .dialog
                .submittingButton

            : USER_TEXT
                .dialog
                .title;


    return (
        <Dialog
            {
            ...COMPONENT_PROPS
                .dialog
            }
            open={
                open
            }
            onClose={
                handleClose
            }
        >
            <DialogTitle
                sx={
                    STYLE.dialogTitle
                }
            >
                <Typography
                    {
                    ...COMPONENT_PROPS
                        .dialogTitleText
                    }
                >
                    {
                        USER_TEXT
                            .dialog
                            .title
                    }
                </Typography>

                <Typography
                    {
                    ...COMPONENT_PROPS
                        .dialogDescription
                    }
                    sx={
                        STYLE
                            .dialogDescription
                    }
                >
                    {
                        USER_TEXT
                            .dialog
                            .description
                    }
                </Typography>
            </DialogTitle>


            <DialogContent
                {
                ...COMPONENT_PROPS
                    .dialogContent
                }
            >
                <Stack
                    {
                    ...COMPONENT_PROPS
                        .dialogStack
                    }
                >
                    <Box>
                        <Typography
                            {
                            ...COMPONENT_PROPS
                                .sectionTitle
                            }
                            sx={
                                STYLE
                                    .userDataSectionTitle
                            }
                        >
                            {
                                USER_TEXT
                                    .dialog
                                    .userDataSection
                            }
                        </Typography>

                        <Grid
                            {
                            ...COMPONENT_PROPS
                                .formGrid
                            }
                        >
                            <UserFormField
                                fieldName={
                                    FORM_FIELD
                                        .USERNAME
                                }
                                form={
                                    form
                                }
                                handleFieldChange={
                                    handleFieldChange
                                }
                                validation={
                                    validation
                                }
                            />

                            <UserFormField
                                fieldName={
                                    FORM_FIELD
                                        .MOBILE_PHONE_NUMBER
                                }
                                form={
                                    form
                                }
                                handleFieldChange={
                                    handleFieldChange
                                }
                                validation={
                                    validation
                                }
                            />

                            <UserFormField
                                fieldName={
                                    FORM_FIELD
                                        .PASSWORD
                                }
                                form={
                                    form
                                }
                                handleFieldChange={
                                    handleFieldChange
                                }
                                validation={
                                    validation
                                }
                            />

                            <UserFormField
                                fieldName={
                                    FORM_FIELD
                                        .REPEATED_PASSWORD
                                }
                                form={
                                    form
                                }
                                handleFieldChange={
                                    handleFieldChange
                                }
                                validation={
                                    validation
                                }
                            />
                        </Grid>
                    </Box>


                    <Box>
                        <Typography
                            {
                            ...COMPONENT_PROPS
                                .sectionTitle
                            }
                            sx={
                                STYLE
                                    .rolesSectionTitle
                            }
                        >
                            {
                                USER_TEXT
                                    .dialog
                                    .rolesSection
                            }
                        </Typography>

                        <Typography
                            {
                            ...COMPONENT_PROPS
                                .sectionDescription
                            }
                            sx={
                                STYLE
                                    .rolesDescription
                            }
                        >
                            {
                                USER_TEXT
                                    .dialog
                                    .rolesDescription
                            }
                        </Typography>

                        <Box
                            sx={
                                roleContainerStyle
                            }
                        >
                            <FormGroup
                                {
                                ...COMPONENT_PROPS
                                    .formGroup
                                }
                            >
                                {
                                    ROLE_CONFIG.map(
                                        (
                                            role,
                                        ) => (
                                            <RoleCheckbox
                                                key={
                                                    role
                                                        .fieldName
                                                }
                                                fieldName={
                                                    role
                                                        .fieldName
                                                }
                                                form={
                                                    form
                                                }
                                                handleRoleChange={
                                                    handleRoleChange
                                                }
                                                label={
                                                    role
                                                        .label
                                                }
                                            />
                                        ),
                                    )
                                }
                            </FormGroup>
                        </Box>

                        {
                            showRoleError
                            && (
                                <Alert
                                    {
                                    ...COMPONENT_PROPS
                                        .roleError
                                    }
                                    sx={
                                        STYLE
                                            .roleError
                                    }
                                >
                                    {
                                        USER_TEXT
                                            .messages
                                            .selectAtLeastOneRole
                                    }
                                </Alert>
                            )
                        }
                    </Box>
                </Stack>
            </DialogContent>


            <DialogActions
                sx={
                    STYLE.dialogActions
                }
            >
                <Button
                    onClick={
                        handleClose
                    }
                    disabled={
                        isSubmitting
                    }
                >
                    {
                        USER_TEXT
                            .dialog
                            .cancelButton
                    }
                </Button>

                <Button
                    {
                    ...COMPONENT_PROPS
                        .submitButton
                    }
                    onClick={
                        createUser
                    }
                    disabled={
                        !canSubmit
                    }
                    startIcon={
                        submitIcon
                    }
                >
                    {
                        submitLabel
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
}


/*
 * =========================================================
 * PAGE
 * =========================================================
 */

export default function Users({
    appAPI,
    contextAPI,
}) {
    const accessToken =
        contextAPI.accessToken
        ?? contextAPI.password;

    const username =
        contextAPI.username;

    const showMessage =
        appAPI.showMessage;


    const {
        loading,
        reloadUsers,
        users,
    } = useUsersPolling({
        accessToken,
        showMessage,
        username,
    });


    const {
        closeDialog,
        isOpen,
        openDialog,
    } = useAddUserDialog();


    const [
        selectedUser,
        setSelectedUser,
    ] = useState(null);

    const [
        editorOpen,
        setEditorOpen,
    ] = useState(false);


    const openEditor =
        useCallback(
            (
                user,
            ) => {
                setSelectedUser(
                    user,
                );

                setEditorOpen(
                    true,
                );
            },
            [],
        );


    const closeEditor =
        useCallback(
            () => {
                setEditorOpen(
                    false,
                );

                /*
                 * Der UserEditor speichert seine Daten selbst.
                 * Beim Schließen laden wir die Tabelle neu,
                 * damit geänderte Namen/Firmendaten direkt
                 * in der Overview-Tabelle sichtbar werden.
                 */
                reloadUsers();
            },
            [
                reloadUsers,
            ],
        );


    return (
        <>
            <Grid
                {
                ...COMPONENT_PROPS
                    .pageGrid
                }
            >
                <Grid
                    {
                    ...COMPONENT_PROPS
                        .fullWidthGridItem
                    }
                >
                    <Box
                        sx={
                            STYLE.topSpacer
                        }
                    />
                </Grid>


                <Grid
                    {
                    ...COMPONENT_PROPS
                        .fullWidthGridItem
                    }
                >
                    <Grid
                        {
                        ...COMPONENT_PROPS
                            .contentGrid
                        }
                    >
                        <Grid
                            {
                            ...COMPONENT_PROPS
                                .fullWidthGridItem
                            }
                        >
                            <Paper
                                sx={
                                    STYLE.header
                                }
                            >
                                <Box
                                    sx={
                                        STYLE
                                            .headerTitleContainer
                                    }
                                >
                                    <PeopleAlt
                                        sx={
                                            STYLE
                                                .headerIcon
                                        }
                                    />

                                    <Typography
                                        {
                                        ...COMPONENT_PROPS
                                            .pageTitle
                                        }
                                    >
                                        {
                                            USER_TEXT
                                                .page
                                                .title
                                        }
                                    </Typography>
                                </Box>

                                {
                                    contextAPI.admin
                                    && (
                                        <Button
                                            {
                                            ...COMPONENT_PROPS
                                                .addButton
                                            }
                                            onClick={
                                                openDialog
                                            }
                                            startIcon={
                                                ADD_ICON
                                            }
                                            sx={
                                                STYLE
                                                    .addButton
                                            }
                                        >
                                            {
                                                USER_TEXT
                                                    .page
                                                    .addButton
                                            }
                                        </Button>
                                    )
                                }
                            </Paper>
                        </Grid>


                        <Grid
                            {
                            ...COMPONENT_PROPS
                                .fullWidthGridItem
                            }
                        >
                            <UserRegister
                                users={
                                    users
                                }
                                loading={
                                    loading
                                }
                                onEdit={
                                    openEditor
                                }
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>


            <AddUser
                accessToken={
                    accessToken
                }
                onClose={
                    closeDialog
                }
                open={
                    isOpen
                }
                reload={
                    reloadUsers
                }
                showMessage={
                    showMessage
                }
            />


            <UserEditorDialog
                appAPI={
                    appAPI
                }
                contextAPI={
                    contextAPI
                }
                user={
                    selectedUser
                }
                open={
                    editorOpen
                }
                onClose={
                    closeEditor
                }
            />
        </>
    );
}